import React, { useState, useEffect } from 'react';
import { 
  Database, 
  Download, 
  Trash2, 
  RefreshCw, 
  Calendar, 
  FileText, 
  HardDrive,
  AlertCircle,
  CheckCircle,
  Clock,
  Settings
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface Backup {
  name: string;
  size: number;
  sizeFormatted: string;
  created: string;
  modified: string;
  type: 'compressed' | 'sql';
}

interface BackupDatabase {
  database_name: string;
  created: string;
}

interface BackupInfo {
  backups: Backup[];
  databases: BackupDatabase[];
  totalBackups: number;
  totalDatabases: number;
  backupDirectory: string;
}

const BackupManagement: React.FC = () => {
  const { user } = useAuth();
  const [backupInfo, setBackupInfo] = useState<BackupInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [showLogs, setShowLogs] = useState(false);
  const [customDate, setCustomDate] = useState('');

  // Check if user has admin privileges
  const hasAdminAccess = user && (user.role === 'admin' || user.role === 'superadmin');

  useEffect(() => {
    if (hasAdminAccess) {
      loadBackupInfo();
    }
  }, [hasAdminAccess]);

  const loadBackupInfo = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/backups', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch backup information');
      }

      const data = await response.json();
      setBackupInfo(data);
    } catch (error: any) {
      console.error('Error loading backup info:', error);
      toast.error('Failed to load backup information');
    } finally {
      setIsLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const response = await fetch('/api/backups/logs?lines=50', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch backup logs');
      }

      const data = await response.json();
      setLogs(data.logs);
    } catch (error: any) {
      console.error('Error loading logs:', error);
      toast.error('Failed to load backup logs');
    }
  };

  const createBackup = async (dryRun = false) => {
    try {
      setIsCreatingBackup(true);
      const response = await fetch('/api/backups/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          customDate: customDate || null,
          dryRun
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create backup');
      }

      const data = await response.json();
      toast.success(dryRun ? 'Dry run completed successfully' : 'Backup process started');
      
      if (!dryRun) {
        // Refresh backup info after a short delay
        setTimeout(() => {
          loadBackupInfo();
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error creating backup:', error);
      toast.error('Failed to create backup');
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const deleteBackup = async (backupName: string) => {
    if (!window.confirm(`Are you sure you want to delete backup "${backupName}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/backups/${encodeURIComponent(backupName)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete backup');
      }

      toast.success('Backup deleted successfully');
      loadBackupInfo();
    } catch (error: any) {
      console.error('Error deleting backup:', error);
      toast.error('Failed to delete backup');
    }
  };

  const deleteBackupDatabase = async (databaseName: string) => {
    if (!window.confirm(`Are you sure you want to delete backup database "${databaseName}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/backups/database/${encodeURIComponent(databaseName)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete backup database');
      }

      toast.success('Backup database deleted successfully');
      loadBackupInfo();
    } catch (error: any) {
      console.error('Error deleting backup database:', error);
      toast.error('Failed to delete backup database');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Check access permissions
  if (!hasAdminAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You need admin privileges to access backup management.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Loading backup information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Backup Management</h1>
              <p className="mt-2 text-gray-600">
                Manage database backups and monitor backup status
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => loadBackupInfo()}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {/* Backup Actions */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Settings className="h-5 w-5 mr-2" />
              Backup Actions
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Custom Date Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Date (Optional)
                </label>
                <input
                  type="date"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Create Backup Button */}
              <div className="flex items-end">
                <button
                  onClick={() => createBackup(false)}
                  disabled={isCreatingBackup}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {isCreatingBackup ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Database className="h-4 w-4" />
                  )}
                  <span>{isCreatingBackup ? 'Creating...' : 'Create Backup'}</span>
                </button>
              </div>

              {/* Dry Run Button */}
              <div className="flex items-end">
                <button
                  onClick={() => createBackup(true)}
                  disabled={isCreatingBackup}
                  className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  <span>Dry Run</span>
                </button>
              </div>

              {/* View Logs Button */}
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setShowLogs(!showLogs);
                    if (!showLogs) {
                      loadLogs();
                    }
                  }}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center space-x-2"
                >
                  <FileText className="h-4 w-4" />
                  <span>{showLogs ? 'Hide Logs' : 'View Logs'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Backup Logs */}
        {showLogs && (
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Backup Logs
              </h2>
            </div>
            <div className="p-6">
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm max-h-96 overflow-y-auto">
                {logs.length > 0 ? (
                  logs.map((log, index) => (
                    <div key={index} className="mb-1">{log}</div>
                  ))
                ) : (
                  <div className="text-gray-500">No logs available</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Backup Statistics */}
        {backupInfo && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <Database className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Backups</p>
                  <p className="text-2xl font-bold text-gray-900">{backupInfo.totalBackups}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <HardDrive className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Backup Databases</p>
                  <p className="text-2xl font-bold text-gray-900">{backupInfo.totalDatabases}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <Calendar className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Latest Backup</p>
                  <p className="text-sm font-bold text-gray-900">
                    {backupInfo.backups.length > 0 
                      ? formatDate(backupInfo.backups[0].created)
                      : 'No backups'
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-orange-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Next Scheduled</p>
                  <p className="text-sm font-bold text-gray-900">1st of next month</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Backup Files */}
        {backupInfo && backupInfo.backups.length > 0 && (
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Download className="h-5 w-5 mr-2" />
                Backup Files
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {backupInfo.backups.map((backup) => (
                    <tr key={backup.name}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {backup.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {backup.sizeFormatted}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          backup.type === 'compressed' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {backup.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(backup.created)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {user.role === 'superadmin' && (
                          <button
                            onClick={() => deleteBackup(backup.name)}
                            className="text-red-600 hover:text-red-900 flex items-center space-x-1"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span>Delete</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Backup Databases */}
        {backupInfo && backupInfo.databases.length > 0 && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Database className="h-5 w-5 mr-2" />
                Backup Databases
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Database Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {backupInfo.databases.map((db) => (
                    <tr key={db.database_name}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {db.database_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(db.created)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {user.role === 'superadmin' && (
                          <button
                            onClick={() => deleteBackupDatabase(db.database_name)}
                            className="text-red-600 hover:text-red-900 flex items-center space-x-1"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span>Delete</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Backups Message */}
        {backupInfo && backupInfo.backups.length === 0 && backupInfo.databases.length === 0 && (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <Database className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Backups Found</h3>
            <p className="text-gray-600 mb-4">
              No backup files or databases found. Create your first backup to get started.
            </p>
            <button
              onClick={() => createBackup(false)}
              disabled={isCreatingBackup}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2 mx-auto"
            >
              {isCreatingBackup ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Database className="h-4 w-4" />
              )}
              <span>{isCreatingBackup ? 'Creating...' : 'Create First Backup'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BackupManagement;
